import { Component, type ReactNode } from 'react';

/**
 * What the app shows when a screen cannot be drawn, instead of nothing.
 *
 * THERE WAS NO SUCH SCREEN until 30 October 2026. A page of the app that failed
 * to load — the usual cause is a phone one deploy behind asking for a file the
 * deploy removed — threw inside React, React unmounted everything, and the
 * player was left looking at a blank page with no way out short of deleting
 * the app from the Home Screen. main.tsx still reloads on a missing file; this
 * is for when that has already been tried, or the error is anything else.
 *
 * The button does what deleting and re-adding did: it clears the app's caches
 * and its service worker, then reloads from the network. Saved scores and
 * sheets live on the server, so it costs nothing that was saved.
 */

export async function resetApp(): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch {
    // No service worker support, or none registered.
  }
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    // No Cache Storage here.
  }
  window.location.reload();
}

export default class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: '2rem 1rem',
          textAlign: 'center',
          background: '#040e20',
          color: '#fff',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>The app needs a refresh</p>
        <p style={{ margin: 0, maxWidth: '22rem', opacity: 0.75, lineHeight: 1.5 }}>
          This screen did not load, usually because a newer version of the app is out. Reloading fetches it.
        </p>
        <button
          type="button"
          onClick={() => void resetApp()}
          style={{
            marginTop: '0.5rem',
            padding: '0.75rem 1.5rem',
            border: 0,
            borderRadius: '999px',
            background: '#fff',
            color: '#040e20',
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Reload the app
        </button>
      </div>
    );
  }
}
